/**
 * Хелперы для SEO настроек и конфигурации.
 * Базовые тексты (siteName, defaultTitle, defaultDescription) берутся из i18n через getLocalizedSeoConfig.
 */

import { getLocalizedSeoConfig, createAlternateLinks, type SupportedLocale, type SeoMessages } from './localeHelpers'
import { getSupportedLocales } from './localeValidation'

const BASE_URL = 'https://bibleawr.com'
const DEFAULT_IMAGE = `${BASE_URL}/assets/images/og-default.png`

export interface BaseSeoConfig {
  siteName?: string
  baseUrl?: string
  defaultImage?: string
  defaultTitle?: string
  defaultDescription?: string
  twitterHandle?: string
  facebookAppId?: string
  locale?: string
  langCode?: string
}

/**
 * Получает базовую конфигурацию SEO из словаря i18n.
 * @param locale — код локали
 * @param messages — сообщения для этой локали (i18n.getLocaleMessage(locale))
 * @param fallback — fallback-сообщения (например, en)
 */
export function getBaseSeoConfig(
  locale: string = 'en',
  messages?: SeoMessages | null,
  fallback?: SeoMessages | null
): BaseSeoConfig {
  const localized = getLocalizedSeoConfig(locale, messages, fallback)
  return {
    siteName: localized.siteName,
    baseUrl: BASE_URL,
    defaultImage: DEFAULT_IMAGE,
    defaultTitle: localized.defaultTitle,
    defaultDescription: localized.defaultDescription,
    twitterHandle: '@bibleawr',
    facebookAppId: undefined,
    locale,
    langCode: localized.langCode,
  }
}

export function getRobotsContent(): string {
  if (process.env.NODE_ENV === 'production') return 'index, follow'
  return 'noindex, nofollow'
}

export function createBasicMetaTags(config: {
  title: string
  description: string
  robots?: string
}): Array<{ name?: string; property?: string; content: string }> {
  const robotsContent = config.robots || getRobotsContent()
  return [
    { name: 'description', content: config.description },
    { name: 'robots', content: robotsContent },
    { name: 'author', content: 'Bible School' },
    { name: 'publisher', content: 'Bible School' },
    { name: 'googlebot', content: robotsContent },
    { name: 'bingbot', content: robotsContent },
  ]
}

/**
 * Создает Open Graph meta теги.
 * @param getConfigForLocale — функция получения конфига для локали (из useSeo: (loc) => getBaseSeoConfig(loc, i18n.getLocaleMessage(loc), i18n.getLocaleMessage('en')))
 */
export function createOpenGraphTags(
  config: {
    title: string
    description: string
    url?: string
    image?: string
    imageAlt?: string
    type?: string
    locale?: string
    siteName?: string
    alternateUrls?: Record<string, string>
  },
  getConfigForLocale: (locale: string) => BaseSeoConfig
): Array<{ property: string; content: string }> {
  const currentLocale = config.locale || 'en'
  const baseConfig = getConfigForLocale(currentLocale)

  const tags = [
    { property: 'og:title', content: config.title },
    { property: 'og:description', content: config.description },
    { property: 'og:type', content: config.type || 'website' },
    { property: 'og:url', content: config.url || baseConfig.baseUrl || '' },
    { property: 'og:image', content: config.image || baseConfig.defaultImage || '' },
    { property: 'og:image:alt', content: config.imageAlt || config.title },
    { property: 'og:site_name', content: config.siteName || baseConfig.siteName || '' },
    { property: 'og:locale', content: baseConfig.langCode || 'en-US' },
    { property: 'article:publisher', content: baseConfig.baseUrl || '' },
  ]

  if (config.alternateUrls) {
    Object.entries(config.alternateUrls).forEach(([locale, _url]) => {
      if (locale !== currentLocale) {
        const altConfig = getConfigForLocale(locale)
        tags.push({ property: 'og:locale:alternate', content: altConfig.langCode || '' })
      }
    })
  }

  return tags
}

export function createTwitterCardTags(
  config: {
    title: string
    description: string
    image?: string
    card?: string
    site?: string
  },
  baseConfig?: BaseSeoConfig
): Array<{ name: string; content: string }> {
  const base = baseConfig ?? { defaultImage: DEFAULT_IMAGE, twitterHandle: '@bibleawr' }
  return [
    { name: 'twitter:card', content: config.card || 'summary_large_image' },
    { name: 'twitter:title', content: config.title },
    { name: 'twitter:description', content: config.description },
    { name: 'twitter:image', content: config.image || base.defaultImage || '' },
    ...(config.site || base.twitterHandle ? [{ name: 'twitter:site', content: config.site || base.twitterHandle || '' }] : []),
  ]
}

export function createCanonicalAndAlternateLinks(
  canonicalUrl: string,
  alternateUrls?: Record<string, string>,
  currentPath?: string
): Array<{ rel: string; href?: string; hreflang?: string }> {
  const links: Array<{ rel: string; href?: string; hreflang?: string }> = []
  links.push({ rel: 'canonical', href: canonicalUrl })

  if (currentPath) {
    const baseUrl = canonicalUrl.split(currentPath)[0] || BASE_URL
    const alternateLinks = createAlternateLinks(baseUrl, currentPath, getSupportedLocales(), alternateUrls)
    alternateLinks.forEach((link) => {
      links.push({ rel: link.rel, hreflang: link.hreflang, href: link.href })
    })
  }

  return links
}

export function formatUrl(path: string, baseUrl?: string): string {
  const base = (baseUrl || BASE_URL).replace(/\/$/, '')
  return path.startsWith('http') ? path : `${base}${path.startsWith('/') ? path : `/${path}`}`
}

/**
 * Валидирует и очищает SEO данные.
 * @param baseConfig — конфиг с defaultTitle/defaultDescription/defaultImage (из getBaseSeoConfig с i18n).
 */
export function sanitizeSeoData(
  data: { title?: string; description?: string; image?: string; url?: string },
  baseConfig: BaseSeoConfig
): { title: string; description: string; image?: string; url?: string } {
  return {
    title: (data.title || baseConfig.defaultTitle || '').trim().slice(0, 60),
    description: (data.description || baseConfig.defaultDescription || '').trim().slice(0, 160),
    image: data.image ? formatUrl(data.image, baseConfig.baseUrl) : baseConfig.defaultImage,
    url: data.url ? formatUrl(data.url, baseConfig.baseUrl) : undefined,
  }
}
